import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { DEFAULT_SKILL_CONTENT } from "~/services/skills";
import {
  SkillEditorForm,
  SkillEditorMode,
  SKILL_INSTRUCTIONS_HEIGHT,
  skillValidationSchema,
  type SkillFormData,
  type SkillFormErrors,
} from "./SkillEditorForm";

const MODAL_TITLES: Record<SkillEditorMode, string> = {
  [SkillEditorMode.CREATE]: "Add New Skill",
  [SkillEditorMode.EDIT]: "Edit Skill",
  [SkillEditorMode.VIEW]: "Skill Details",
};

// Placeholder shown while an existing skill's content is being fetched, so the
// modal opens with a stable skeleton instead of flashing default content and
// then swapping in the real values. Mirrors the form's field layout.
const SkillEditorSkeleton = () => (
  <div className="flex-1 overflow-y-auto px-6 pb-4 pt-4">
    <div className="space-y-4">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-9 w-full" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-full" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className={cn(SKILL_INSTRUCTIONS_HEIGHT, "w-full")} />
      </div>
    </div>
  </div>
);

interface SkillEditorModalProps {
  errors?: SkillFormErrors;
  loading?: boolean;
  // True while an existing skill's content is being fetched. Renders a skeleton
  // body in place of the form until the data arrives.
  contentLoading?: boolean;
  onClose: () => void;
  onSubmit: (data: SkillFormData) => void | Promise<void>;
  open: boolean;
  initialData?: Partial<SkillFormData>;
  mode?: SkillEditorMode;
}

export const SkillEditorModal = ({
  errors = {},
  loading = false,
  contentLoading = false,
  onClose,
  onSubmit,
  open,
  initialData,
  mode = SkillEditorMode.CREATE,
}: SkillEditorModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<SkillFormData>({
    resolver: yupResolver(skillValidationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      content: DEFAULT_SKILL_CONTENT,
    },
  });

  // Repopulate when the dialog opens or initialData resolves (Edit fetches the
  // skill's content asynchronously after the modal is already open).
  useEffect(() => {
    if (open) {
      reset({
        name: initialData?.name ?? "",
        description: initialData?.description ?? "",
        content: initialData?.content ?? DEFAULT_SKILL_CONTENT,
      });
    }
  }, [open, initialData, reset]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100%_-_64px)] sm:max-w-[900px]">
        <DialogHeader className="shrink-0 px-6 pb-2 pt-6">
          <DialogTitle>{MODAL_TITLES[mode]}</DialogTitle>
        </DialogHeader>

        {contentLoading ? (
          <SkillEditorSkeleton />
        ) : (
          <SkillEditorForm control={control} errors={errors} loading={loading} mode={mode} />
        )}

        <DialogFooter className="shrink-0 px-6 pb-4">
          {mode === SkillEditorMode.VIEW ? (
            <Button onClick={onClose} size="sm" className="min-w-[72px]">
              Close
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} disabled={loading} size="sm">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || loading || contentLoading}
                loading={loading}
                size="sm"
                className="min-w-[72px]"
              >
                {mode === SkillEditorMode.EDIT ? "Save" : "Add"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
